import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, ArrowRight, LayoutDashboard, Wrench, FileText, Building, User, Receipt, AlertCircle, Briefcase, Wallet, Key, Hash } from 'lucide-react';
import { useNavigate } from '../../routes';
import { useProperties, useUnits } from '../../hooks/queries/useProperties';
import { useTenants } from '../../hooks/queries/useTenants';
import { useTickets } from '../../hooks/queries/useMaintenance';
import { useBills } from '../../hooks/queries/useAccounting';
import { useApplications, useLeases } from '../../hooks/queries/useLeasing';
import { useVendors, useOwners, useInvestors } from '../../hooks/queries/useCore';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { performGlobalSearch, prepareSearchableEntities, getHighlightSegments, getSearchSuggestions, SearchResult } from '../../services/ui/globalSearchService';
import { searchApi } from '../../services/api/search';
import { logger } from '@repo/shared-utils';
import { useAppStore } from '../../store';
import { PORTAL_EMPTY_STATES } from '@/constants/portalEmptyStates';
import { getNavSchemaForRole } from '@repo/shared-config';
import { useOrgNavFeatures } from '../../hooks/useOrgNavFeatures';

interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
  // onChangeView removed, uses hook internally
}

type CommandItem = {
  id: string;
  label: string;
  subtitle?: string;
  description?: string;
  icon: React.ElementType;
  group: string;
  route?: string;
  type: string;
  relevanceScore: number;
  /** Descending sort key (0–100) for hybrid API + client search */
  unifiedScore?: number;
  matches?: Array<{ field: string; value: string; score: number }>;
  context?: Record<string, string>;
};

// Icon mapping for entity types
const ENTITY_ICONS: Record<string, React.ElementType> = {
  property: Building,
  tenant: User,
  application: FileText,
  bill: Receipt,
  ticket: Wrench,
  vendor: Briefcase,
  owner: Wallet,
  investor: Wallet,
  lease: Key,
  unit: Hash
};

const SEARCH_GROUP_LABELS: Record<string, string> = {
  property: 'Properties',
  tenant: PORTAL_EMPTY_STATES.command.categoryResidents,
  application: 'Applications',
  bill: 'Invoices',
  ticket: 'Maintenance',
  vendor: 'Vendors',
  owner: 'Owners',
  investor: 'Investors',
  lease: 'Leases',
  unit: 'Units',
};

export const CommandPalette: React.FC<CommandPaletteProps> = ({ isOpen, onClose }) => {
  const userRole = useAppStore((s) => s.userRole);
  const isSelfManagingOwner = useAppStore((s) => s.isSelfManagingOwner);
  const orgNavFeatures = useOrgNavFeatures();
  // Fetch all data for global search
  const staticCommands = useMemo<CommandItem[]>(() => {
    const schema = getNavSchemaForRole(userRole, {
      isSelfManagingOwner: Boolean(isSelfManagingOwner),
      orgFeatures: orgNavFeatures,
    });
    const sectionCommands: CommandItem[] = [];

    for (const section of schema) {
      const route = section.items?.[0]?.route ?? '/app';
      sectionCommands.push({route, id: section.id, label: `Go to ${section.label}`, icon: section.icon ?? LayoutDashboard, group: 'Navigation', type: 'command', relevanceScore: 1});

      for (const item of section.items ?? []) {
        sectionCommands.push({
          id: item.id,
          label: item.label,
          icon: section.icon ?? LayoutDashboard,
          group: section.label,
          route: item.route,
          type: 'command',
          relevanceScore: 1,
        });
      }
    }

    // De-duplicate by id to avoid noisy command results for shared route aliases.
    const unique = new Map<string, CommandItem>();
    for (const command of sectionCommands) {
      if (!unique.has(command.id)) unique.set(command.id, command);
    }
    return Array.from(unique.values());
  }, [userRole, isSelfManagingOwner, orgNavFeatures]);

  const { data: properties = [] } = useProperties();
  const { data: tenants = [] } = useTenants();
  const { data: tickets = [] } = useTickets();
  const { data: bills = [] } = useBills();
  const { data: applications = [] } = useApplications();
  const { data: vendors = [] } = useVendors();
  const { data: owners = [] } = useOwners();
  const { data: investors = [] } = useInvestors();
  const { data: leases = [] } = useLeases();
  const { data: units = [] } = useUnits();

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebouncedValue(query, 300);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const { data: apiSearchHits = [], isFetching: isApiSearchFetching } = useQuery({
    queryKey: ['global-search', debouncedQuery.trim()],
    queryFn: () => searchApi.global(debouncedQuery.trim()),
    enabled: isOpen && debouncedQuery.trim().length >= 2,
    staleTime: 30_000,
  });

  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Prepare searchable entities
  const searchableEntities = useMemo(() => {
    return prepareSearchableEntities({
      properties,
      tenants,
      applications,
      bills,
      tickets,
      vendors,
      owners,
      investors,
      leases,
      units
    }, {
      properties,
      units,
      tenants,
      vendors
    });
  }, [properties, tenants, applications, bills, tickets, vendors, owners, investors, leases, units]);

  // Hybrid: GET /search for tenant/property/owner + client fuzzy for other entities; sort by unifiedScore
  const searchResults = useMemo<CommandItem[]>(() => {
    const q = debouncedQuery.trim();
    if (!q || q.length < 2) {
      return staticCommands;
    }

    const clientResults = performGlobalSearch(q, searchableEntities, {
      maxResults: 20,
      minScore: 0.3,
    }).filter(
      (r) =>
        r.entity.type !== 'property' &&
        r.entity.type !== 'tenant' &&
        r.entity.type !== 'owner',
    );

    const clientItems: CommandItem[] = clientResults.map((result: SearchResult) => {
      const Icon = ENTITY_ICONS[result.entity.type] || AlertCircle;
      const unifiedScore = Math.min(100, Math.round(Math.min(1, result.relevanceScore) * 100));
      return {
        id: `${result.entity.type}-${result.entity.id}`,
        label: result.entity.title,
        subtitle: result.entity.subtitle,
        description: result.entity.description,
        icon: Icon,
        group: SEARCH_GROUP_LABELS[result.entity.type] || 'Other',
        context: { [`${result.entity.type}Id`]: result.entity.id },
        route: result.entity.route,
        type: result.entity.type,
        relevanceScore: result.relevanceScore,
        unifiedScore,
        matches: result.matches,
      };
    });

    const apiItems: CommandItem[] = apiSearchHits.map((hit) => {
      const Icon = ENTITY_ICONS[hit.type] || AlertCircle;
      return {
        id: `${hit.type}-${hit.id}`,
        label: hit.title,
        subtitle: hit.subtitle,
        description: undefined,
        icon: Icon,
        group: SEARCH_GROUP_LABELS[hit.type] || 'Other',
        context: { [`${hit.type}Id`]: hit.id },
        route: hit.route,
        type: hit.type,
        relevanceScore: hit.unifiedScore / 100,
        unifiedScore: hit.unifiedScore,
        matches: undefined,
      };
    });

    return [...apiItems, ...clientItems].sort((a, b) => {
      const ua = a.unifiedScore ?? Math.min(100, Math.round((a.relevanceScore ?? 0) * 100));
      const ub = b.unifiedScore ?? Math.min(100, Math.round((b.relevanceScore ?? 0) * 100));
      if (ub !== ua) return ub - ua;
      return (a.label || '').localeCompare(b.label || '');
    });
  }, [debouncedQuery, searchableEntities, staticCommands, apiSearchHits]);

  const searchListResetKey = `${debouncedQuery}|${searchResults.length}`;
  const [prevSearchListKey, setPrevSearchListKey] = useState(searchListResetKey);
  if (searchListResetKey !== prevSearchListKey) {
    setPrevSearchListKey(searchListResetKey);
    setSelectedIndex(0);
  }

  // Get search suggestions for autocomplete
  const suggestions = useMemo(() => {
    if (!query || query.length < 2) return [];
    return getSearchSuggestions(query, searchableEntities, 5);
  }, [query, searchableEntities]);

  const handleSelect = React.useCallback((cmd: CommandItem) => {
    if (cmd.route) {
      navigate(cmd.route);
    } else {
      // Fallback for search results that may not have route set
      logger.warn('Command missing route:', cmd);
    }
    onClose();
    setQuery('');
    setSelectedIndex(0);
  }, [navigate, onClose]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => (prev + 1) % searchResults.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => (prev - 1 + searchResults.length) % searchResults.length);
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (searchResults[selectedIndex]) {
          handleSelect(searchResults[selectedIndex]);
        }
      } else if (e.key === 'Escape') {
        onClose();
        setQuery('');
        setSelectedIndex(0);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, searchResults, selectedIndex, handleSelect, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[20vh] px-4">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity" onClick={onClose}></div>

      {/* Palette */}
      <div className="bg-white dark:bg-slate-900 w-full max-w-xl rounded-xl shadow-2xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 animate-slide-up relative z-10 flex flex-col max-h-[60vh]">
        <div className="flex items-center border-b border-slate-200 dark:border-slate-800 p-4">
          <Search size={20} className="text-slate-400 mr-3" />
          <input
            ref={inputRef}
            type="text"
            className="flex-1 bg-transparent border-none focus:ring-0 text-slate-900 dark:text-white placeholder:text-slate-400 text-lg"
            placeholder="Type a command or search..."
            value={query}
            onChange={(e) => { setQuery(e.target.value); setSelectedIndex(0); }}
          />
          <button onClick={onClose} className="bg-slate-100 dark:bg-slate-800 p-1 rounded text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
            <span className="text-xs font-bold px-1">ESC</span>
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {searchResults.length > 0 ? (
            <div className="space-y-1">
              {searchResults.map((cmd, index) => {
                const Icon = cmd.icon;
                return (
                  <button
                    key={`${cmd.id}-${cmd.type || 'command'}-${index}`}
                    onClick={() => handleSelect(cmd)}
                    className={`w-full flex items-center justify-between px-3 py-3 rounded-lg text-left transition ${index === selectedIndex
                      ? 'bg-brand-50 dark:bg-brand-900/20 text-brand-700 dark:text-brand-300'
                      : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'
                      }`}
                    onMouseEnter={() => setSelectedIndex(index)}
                  >
                    <div className="flex items-center min-w-0 flex-1">
                      <div className={`p-2 rounded-md mr-3 shrink-0 ${index === selectedIndex ? 'bg-brand-100 dark:bg-brand-900/40' : 'bg-slate-100 dark:bg-slate-800'}`}>
                        <Icon size={18} />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="font-medium truncate">
                          {cmd.matches
                            ? getHighlightSegments(cmd.label, query).map((seg, i) =>
                                seg.highlight ? (
                                  <mark key={i} className="bg-amber-200 dark:bg-amber-900/50 px-0.5 rounded">
                                    {seg.text}
                                  </mark>
                                ) : (
                                  <React.Fragment key={i}>{seg.text}</React.Fragment>
                                )
                              )
                            : cmd.label}
                        </div>
                        {cmd.subtitle && (
                          <div className="text-xs opacity-70 truncate">
                            {cmd.subtitle}
                          </div>
                        )}
                        {cmd.description && (
                          <div className="text-xs opacity-50 truncate mt-0.5">
                            {cmd.description}
                          </div>
                        )}
                        <div className="text-xs opacity-60 mt-0.5">{cmd.group}</div>
                      </div>
                      {typeof cmd.relevanceScore === 'number' && cmd.relevanceScore < 1 && (
                        <div className="text-xs text-slate-400 mr-2 shrink-0">
                          {Math.round(cmd.relevanceScore * 100)}%
                        </div>
                      )}
                    </div>
                    {index === selectedIndex && <ArrowRight size={16} className="opacity-50 shrink-0 ml-2" />}
                  </button>
                );
              })}
            </div>
          ) : debouncedQuery.trim().length >= 2 ? (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              {isApiSearchFetching ? (
                <p>Searching…</p>
              ) : (
                <p>No results found for &quot;{debouncedQuery.trim()}&quot;</p>
              )}
              {suggestions.length > 0 && (
                <div className="mt-4">
                  <p className="text-xs mb-2">Did you mean:</p>
                  <div className="flex flex-wrap gap-2 justify-center">
                    {suggestions.map((suggestion, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setQuery(suggestion);
                          setSelectedIndex(0);
                        }}
                        className="px-3 py-1 text-xs bg-slate-100 dark:bg-slate-800 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-700 transition"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="p-8 text-center text-slate-500 dark:text-slate-400">
              <p>Start typing to search across all modules...</p>
              <p className="text-xs mt-2">Search properties, tenants, applications, bills, work orders, and more</p>
            </div>
          )}
        </div>

        <div className="bg-slate-50 dark:bg-slate-950 p-2 text-xs text-slate-400 flex justify-between px-4 border-t border-slate-200 dark:border-slate-800">
          <span><span className="font-bold">↑↓</span> to navigate</span>
          <span><span className="font-bold">↵</span> to select</span>
        </div>
      </div>
    </div>
  );
};

